import type { ObjectTypesSource, PickUpRefusalReason, ReadingLayout } from "odyssey-shared";
import type { HarvestReading } from "./harvest/store.ts";
import type { TravelSnapshot } from "./travel/store.ts";
import type { KeyboardOwner } from "../ui/input/router.ts";
import { createAudioReport, type AudioReport } from "./audio/report.ts";
import type { BagSnapshot } from "./bag/store.ts";
import type { WalletSnapshot } from "./wallet/store.ts";
import type { Chest } from "./chest/lines.ts";
import type { HudVitals } from "./hud/store.ts";
import type { PlayerName } from "./player-names.ts";
import type { GroundReading } from "./ground/view.ts";
import { PAGE_RULES } from "./rules.ts";
import { createBubbleReport, type BubbleReport } from "./world-ui/bubbles/report.ts";
import { createEmojiReport, type EmojiReport } from "./world-ui/emoji/report.ts";
import { createVfxReport, type VfxReport } from "./world-ui/vfx/report.ts";
import { createDamageNumberReport, type DamageNumberReport } from "./world-ui/damage-numbers/report.ts";
import { createProjectileReport, type ProjectileReport } from "./world/projectile-report.ts";
import { createHealthBarReport, type HealthBarReport } from "./world-ui/health-bars/report.ts";
import { createCreaturePlateReport, type CreaturePlateReport } from "./world-ui/creature-plates/report.ts";
import { createGroundLabelReport, type GroundLabelReport } from "./world-ui/ground-labels/report.ts";
import { createNameplateReport, type NameplateReport } from "./world-ui/nameplates/report.ts";
import { createPromptReport, type PromptReport } from "./world-ui/prompt/report.ts";
import { createLightingControl, type LightingControl } from "./world-ui/lighting/control.ts";
import { createSharpZoomControl, type SharpZoomControl } from "./world-ui/sharp-zoom/control.ts";
import type { NeighbourReading } from "./neighbours/ground.ts";

/**
 * The look a player in the view holds, as it arrived in a `looks` frame (ADR-094 decision 6): its handle and each
 * slot's piece number.
 */
export interface PlayerLook {
  readonly handle: number;
  readonly skin: number;
  readonly hair: number;
  readonly flags: number;
  readonly outfit: number;
  readonly hat: number;
  readonly mainHand: number;
  readonly offHand: number;
}

/**
 * The death screen as the report reads it.
 */
export interface DeathReport {
  readonly shown: boolean;
  readonly fade: boolean;
  readonly cause: string | null;
  readonly place: string | null;
  readonly ready: boolean;
  readonly cues: number;
}

/**
 * Measurements the game page keeps, exposed as `window.game` on a development page alone (`isDevelopmentPage`) for
 * browser tests, and read by the diagnostic strip. `transitions`
 * counts the transition animations the page has started playing (ADR-058) and `presenceTransitions` the strips
 * its roofs have started as the local character walks in and out (ADR-058 amendment, P1.3-T4); `thingFrames`
 * holds, by handle, the frame id each thing in view shows this render frame, a transition's frame while one
 * plays, written only when it changes; `shrines` holds the handles of the shrines the page's character has discovered,
 * as the session's last `shrines` told them, and `shrinePops` counts the pops the page has started (ADR-096);
 * `largestCorrection` is the largest position error, in sixteenths, of any correction the page has made.
 * `death` is the death screen as the page shows it (ADR-073's amendment "the respawn is the player's"), read from its
 * store as the report is read: whether it shows and fades in, its cause line, the place its button names, whether the
 * button is enabled, and how many times `player_death` has fired.
 * `keyboard` is who owns the keyboard (the input router's owner, read from the router as the report is read)
 * and `menuRequests` counts the times the router reported Escape as a request for the game menu, whether or
 * not the menu could open (behind the loading shell it opens nothing, P1.9-T14). `message` is the message
 * line's text, mirrored from the HUD's store as it changes, and `hudRenders` counts the commits of the HUD's
 * parts (P1.9-T13), which stay flat while nothing it shows changes.
 * `sharpZoom` is the control of the sharp scaling `world-ui/modules.ts` lists (CLIENT.md 7.6): the plan of the
 * frame last drawn, and the switch a browser test turns off to draw an in-between zoom with the world camera's
 * nearest sampling, as the gallery's `sharp-zoom` page does.
 *
 * `lighting` is the light layer's control (`world-ui/lighting/`, ADR-141's amendment of 2026-10-31): the sky override a
 * server or GM hook switches on, the development switches and what the last frame drew.
 *
 * `ready` is the loading shell's reveal (P1.4-T5): true once a drawn frame showed the local character over
 * every chunk its camera saw, which is after `welcomed`, the server's welcome. `drawn` is what the last drawn
 * frame's camera showed, in world pixels, and the chunk range painted then (`painted` false before any), so a
 * test can check each frame against the chunks its view needed.
 *
 * `playerLooks` reads the look each player in the view holds, its own included, as the `looks` frames carried them
 * (ADR-094 decision 6), in handle order; a player whose look has not arrived is not listed.
 *
 * `vitals` is the player's own exact values as the last `vitals` frame carried them, mirrored from the HUD's store
 * (ADR-074 decision 5), null before the first. `combatEvents` counts the events the page's `combat` frames carried
 * (decision 3), which P2.1-T4 draws. `target` is the handle the page selects, -1 for none (P2.2-T3). `slotKey` is the
 * index of the ability bar's slot the last slot key named, from 0, whether or not it sent an act, -1 before any
 * (P2.2-T11). `stance` is the weapon's stance, `drawn` or `sheathed` (P2.2-T13).
 *
 * `rulesVersion` is the version of the rules this page was built with, which a `welcome` naming others reloads the
 * page for (ADR-069 decision 8). `worldSource` says where the loaded world's version came from (ADR-050's amendment
 * of 2026-10-02): the gateway's `/status`, a `welcome` before the last reload, or the pointer; null before it loads.
 *
 * `name` is the player's own first and family names as `welcome` carried them, null before a welcome, and
 * `players` the names of every player the view holds, the player's own included, each part apart, refreshed
 * whenever a snapshot could have changed them (ADR-067). `nameplates` is the nameplates' report and switch
 * (P1.9-T16, `world-ui/nameplates/report.ts`), `healthBars` and `damageNumbers` the health bars' and the damage
 * numbers' pools and counts (P2.1-T4, `world-ui/health-bars/report.ts`, `world-ui/damage-numbers/report.ts`),
 * `creaturePlates` the creature nameplates' (R-0089, `world-ui/creature-plates/report.ts`), `groundLabels` the ground
 * item labels' pool, counts and the hold of the key that shows every label (P2.8-T76, `world-ui/ground-labels/report.ts`),
 * `projectiles` the projectile view's pools, counts and looks, with the way a browser suite hands it launches
 * (P2.4-T22, `world/projectile-report.ts`),
 * `bubbles` the chat bubbles' report, switch and queue of Local
 * lines, which the chat store fills (P1.9-T23, `world-ui/bubbles/report.ts`), `emoji` the emoji over characters'
 * report, switches and queue, which the connection's `emojiShown` fills (P2.19-T7, `world-ui/emoji/report.ts`), and
 * `prompt` the interaction prompt's
 * report and switch: what it names, the labels it has set and the target lookup's scans (P1.9-T17,
 * `world-ui/prompt/report.ts`). `reading` is the reading panel's state (P1.9-T37): whether it is open, the thing and
 * the text it shows and how it reads when long (ADR-077), and how many readings it has opened or replaced, which the
 * page writes on each change. `creatures` lists every creature the page draws (ADR-082), with where it stands in
 * sixteenths, its facing and the strip it shows, built only when read; `minimapGround` the minimap's map pictures, built only
 * when read: how many it holds, their memory in bytes, the chunks each owes and how many chunks have been composed in all
 * and the most one step composed (P1.9-T178, `map-picture/pictures.ts`); `minimapOthers` the other players and creatures the minimap marks (P1.9-T179), built only when read; `concealment` lists every remote entity drawn
 * with the scene's judgement of whether the viewer can see it (ADR-061's amendment for P1.9-T73), also built only when
 * read, and `concealJudged` counts the cover measurements made; `sightHeading` is the local character's cone of sight's
 * axis in radians as eased this frame and `sightDimmed` how many props the cone holds below full alpha (ADR-061's
 * amendment for P1.9-T75); `sightCover` is the cover around the local character at its last measurement (the art of
 * the props the cone could dim inside the square around its feet, over the square's area), `sightDense` whether that
 * place counts as dense, by the knobs' hysteresis, and `sightStrength` the cone's strength in `[0, 1]` as eased this
 * frame (ADR-061's amendment for P1.9-T82); `keptCover` is the share of the local character's body building parts hide together and
 * `roofCover` the largest share one closed roof hides of it, which withholds the silhouette, and
 * `silhouette` whether its silhouette shows through it (ADR-040's amendment of 2026-10-03, P1.9-T78); `weather` what the rain draws (`WeatherReport`, P2.27-T2); `remote`, `remoteX` and `remoteStrip` count and
 * read the other players' characters alone, `remoteX` in whole sixteenths, rounded; `localLift` and `remoteLift` are how
 * far the own character and the first other player are drawn above their feet, a hop's lift (ADR-116's third
 * amendment); `renderLead` is the render clock less the
 * newest tick applied, in ticks, which above `RENDER_DELAY_TICKS` means remotes are drawn past the newest snapshot
 * (ADR-032's amendment of 2026-10-03). `audio` is the sound's report (ADR-085 decision 12, `audio/report.ts`): the
 * sounds asked for and started, whether the context is unlocked and the music track playing. `bag` is the player's
 * bag as the Inventory window draws it (P2.8-T11): whether the window is open, the size the server named and each slot.
 * `wallet` is what the server last said the character holds (P2.8-T38), each currency by its code in its smallest
 * unit, or null before a `wallet` has arrived.
 * `chest` is the chest the loot window shows (P2.8-T16), with each line and its marks, or null while it is closed.
 * `ground` is each ground item drawn (P2.8-T65), its item, look and point in world pixels, as the last snapshot placed
 * them, and its lift (P2.8-T74), which each render frame keeps current; `pickUp` the handles of the newest `pickUp`
 * requests sent and the reasons of the newest refusals. `neighbours` reads the ground drawn beside the map the page stands
 * on (ADR-147 decisions 6 to 10, `neighbours/ground.ts`) when a test asks: each neighbour's chunks loaded and on their way,
 * its pages and whether its painter draws, the chunks fetched and let go, and the camera's bounds in tiles; null between
 * stages.
 */
export interface GameReport {
  ready: boolean;
  welcomed: boolean;
  error: string | null;
  closed: string | null;
  chunksLoaded: number;
  neighbours: () => NeighbourReading | null;
  /** How many seamless crossings this page has taken (ADR-147 decision 18). */
  crossings: number;
  /** How long the last seamless crossing's change of stage took, in milliseconds, or -1 before one. */
  restageMs: number;
  /** How many figures a crossing holds now (ADR-147 decision 15). */
  edgeHeld: () => number;
  /**
   * Every figure of a first name the page draws, in world pixels from the world's origin, and which collection draws it
   * (`own`, `owner`, `edge` or `held`), so a suite sees one continuous figure through a crossing (ADR-147 decision 15).
   */
  figuresNamed: (first: string) => { readonly x: number; readonly y: number; readonly from: string }[];
  handle: number;
  tick: number;
  snapshots: number;
  localX: number;
  localY: number;
  remote: number;
  speed: number;
  corrections: [number, number, number, number];
  largestCorrection: number;
  inputsSkipped: number;
  snapshotsDiscarded: number;
  vitals: HudVitals | null;
  combatEvents: number;
  target: number;
  slotKey: number;
  stance: "drawn" | "sheathed";
  debug: boolean;
  localStrip: string;
  localLift: number;
  remoteX: number | null;
  remoteStrip: string | null;
  remoteLift: number | null;
  renderLead: number;
  interaction: { readonly handle: number; readonly verb: string; readonly ok: boolean; readonly text: string } | null;
  message: string | null;
  reading: { readonly open: boolean; readonly handle: number; readonly text: string; readonly layout: ReadingLayout; readonly opened: number };
  bag: BagSnapshot;
  wallet: WalletSnapshot;
  chest: Chest | null;
  things: readonly { readonly handle: number; readonly kind: number; readonly state: number }[];
  ground: readonly GroundReading[];
  pickUp: { readonly sent: number[]; readonly refused: PickUpRefusalReason[] };
  thingFrames: Record<number, string>;
  shrines: readonly number[];
  shrinePops: number;
  transitions: number;
  presenceTransitions: number;
  pvp: number;
  worldVersion: string | null;
  worldSource: "status" | "welcome" | "pointer" | null;
  readonly rulesVersion: string;
  objectTypes: ObjectTypesSource | null;
  keyboard: KeyboardOwner;
  death: DeathReport;
  menuRequests: number;
  hudRenders: number;
  name: { readonly firstName: string; readonly familyName: string } | null;
  players: readonly PlayerName[];
  playerLooks: () => readonly PlayerLook[];
  creatures: () => readonly CreatureReading[];
  concealment: () => readonly ConcealReading[];
  minimapGround: () => MinimapGroundReading | null;
  puddles: () => { readonly wet: number; readonly puddles: number; readonly shown: number; readonly groundTiles: number; readonly list: readonly { readonly x: number; readonly y: number; readonly radius: number; readonly cls: string }[]; readonly drips: number; readonly dripDrops: number } | null;
  /** The move speed the local character's prediction steps at, the sum of its confirmed effects' `moveSpeed` in whole percents (ADR-144's amendment of 2026-11-01). */
  moveSpeed: number;
  minimapOthers: () => MinimapOthersReading;
  worldMap: () => WorldMapReport;
  /** The page's waypoint travel: the offer shown, the line picked and the channel bar (P2.11-T38). */
  travel: () => TravelSnapshot | null;
  /** The page's harvesting: the props the server says are gone and the cast bar (ADR-142 decision 3, P2.26-T3). */
  harvest: () => HarvestReading | null;
  /** The page's tool belt: whether the server's belt arrived, its four slots and the selection (ADR-142 decision 3b, P2.26-T17). */
  belt: () => { readonly arrived: boolean; readonly slots: readonly (string | null)[]; readonly selected: number } | null;
  concealJudged: number;
  sightHeading: number;
  sightDimmed: number;
  sightCover: number;
  sightDense: boolean;
  sightStrength: number;
  keptCover: number;
  roofCover: number;
  silhouette: boolean;
  weather: WeatherReport;
  audio: AudioReport;
  readonly drawn: DrawnView;
  readonly sharpZoom: SharpZoomControl;
  readonly lighting: LightingControl;
  readonly nameplates: NameplateReport;
  readonly healthBars: HealthBarReport;
  readonly creaturePlates: CreaturePlateReport;
  readonly groundLabels: GroundLabelReport;
  readonly damageNumbers: DamageNumberReport;
  readonly projectiles: ProjectileReport;
  readonly bubbles: BubbleReport;
  readonly emoji: EmojiReport;
  /** The vfx surface's switch, counts and the vfx running (P2.4-T41, ADR-134, `world-ui/vfx/report.ts`). */
  readonly vfx: VfxReport;
  readonly prompt: PromptReport;
}

/**
 * What one drawn frame's camera showed and what was painted: the view in world pixels, the chunk range
 * painted (meaningful only while `painted`), and whether the local character existed.
 */
export interface DrawnView {
  x: number;
  y: number;
  width: number;
  height: number;
  painted: boolean;
  minCx: number;
  minCy: number;
  maxCx: number;
  maxCy: number;
  character: boolean;
}

/**
 * One creature the page draws: its handle, its appearance, where it stands in sixteenths, its facing and the strip it
 * shows.
 */
export interface CreatureReading {
  readonly handle: number;
  readonly appearance: number;
  readonly x: number;
  readonly y: number;
  readonly facing: number;
  readonly strip: string;
}

/**
 * The minimap's map pictures as the page reports them: how many it holds, their memory, the chunks each field's picture
 * still owes, the chunks composed in all, the shrine art held for the pictures and the landmarks they could not place.
 */
export interface MinimapGroundReading {
  readonly pictures: number;
  readonly bytes: number;
  readonly owedHud: number;
  readonly owedNearby: number;
  readonly composed: number;
  readonly most: number;
  readonly shrineArt: number;
  readonly skippedLandmarks: number;
}

/**
 * The minimap's marks of other players and creatures as the page reports them (P1.9-T179): the handles marked of each
 * kind, in the order added, and how many marks the buffer could not hold.
 */
export interface MinimapOthersReading {
  readonly players: readonly number[];
  readonly creatures: readonly number[];
  readonly dropped: number;
}

/**
 * The world map's state for the browser tests and the measurement (P2.12-T6, ADR-100 decision 3), built only when read:
 * whether its window is open and drawing, how many times its view was asked to repaint, the chunks drawn and what
 * drawing them cost in milliseconds frame by frame, the places and the pin it holds, whether the view follows the
 * character, the view's origin, how many of the picture's fog cells are explored, and the chunks drawn as `cx,cy` in the order they were drawn. `level` and `step` are the Map's level and Nearby step (P2.12-T21) and `presentation` how it is shown, window, float or popped out (P2.12-T23); `regionWaitMs` is the milliseconds from the last zoom out that landed at Region to its first picture being ready to show, or -1 (P2.12-T32).
 */
export interface WorldMapReport {
  readonly open: boolean;
  readonly ready: boolean;
  readonly presentation: "window" | "float" | "popped";
  readonly level: "nearby" | "region" | "world";
  readonly step: number;
  readonly drawing: boolean;
  readonly draws: number;
  readonly chunks: number;
  readonly frames: number;
  readonly drawMs: number;
  readonly longestMs: number;
  readonly spanMs: number;
  readonly done: boolean;
  readonly places: readonly string[];
  readonly pin: { readonly x: number; readonly y: number } | null;
  readonly following: boolean;
  readonly origin: { readonly left: number; readonly top: number };
  readonly explored: number;
  readonly order: readonly string[];
  readonly regionWaitMs: number;
}

/**
 * The world map's report before the page has one.
 */
export const EMPTY_WORLD_MAP_REPORT: WorldMapReport = { open: false, ready: false, presentation: "window", level: "region", step: 1, drawing: false, draws: 0, chunks: 0, frames: 0, drawMs: 0, longestMs: 0, spanMs: 0, done: false, places: [], pin: null, following: true, origin: { left: 0, top: 0 }, explored: 0, order: [], regionWaitMs: -1 };

/**
 * One remote entity's concealment as the scene judged it this frame: the largest share of its body one prop drawn
 * above it hides and the largest one building part hides (`kept`, P1.9-T78), whether it stands under a roof the viewer
 * is not under, whether its feet stand in the local character's cone of sight (P1.9-T75, which sets aside every share
 * but `kept`), whether it is concealed (with the hysteresis),
 * and whether the world UI drawn for it hides (concealed and not targeted).
 */
export interface ConcealReading {
  readonly handle: number;
  readonly coverage: number;
  readonly kept: number;
  readonly roofed: boolean;
  readonly sighted: boolean;
  readonly concealed: boolean;
  readonly hidden: boolean;
}

/**
 * What the weather draws now: how many rain emitters run, the pool budget of those emitters (halved at Effects Reduced) and
 * the dim laid over the world, 0 to 1 (P2.27-T2), and how many wind wisps, gust dashes and cloud shadows are drawn (P2.27-T24).
 */
export interface WeatherReport {
  emitters: number;
  budget: number;
  dim: number;
  wisps: number;
  dashes: number;
  clouds: number;
  strength: number;
  wet: number;
  fading: boolean;
  water: number;
}

/**
 * The live report.
 */
export const gameReport: GameReport = {
  ready: false,
  welcomed: false,
  error: null,
  closed: null,
  chunksLoaded: 0,
  neighbours: () => null,
  crossings: 0,
  restageMs: -1,
  edgeHeld: () => 0,
  figuresNamed: () => [],
  handle: -1,
  tick: 0,
  snapshots: 0,
  localX: 0,
  localY: 0,
  remote: 0,
  speed: 0,
  corrections: [0, 0, 0, 0],
  largestCorrection: 0,
  inputsSkipped: 0,
  snapshotsDiscarded: 0,
  vitals: null,
  combatEvents: 0,
  target: -1,
  slotKey: -1,
  stance: "sheathed",
  debug: false,
  localStrip: "",
  localLift: 0,
  remoteX: null,
  remoteStrip: null,
  remoteLift: null,
  renderLead: 0,
  interaction: null,
  message: null,
  reading: { open: false, handle: -1, text: "", layout: "scroll", opened: 0 },
  bag: { open: false, size: null, slots: [], kept: [], selected: null, moving: null, slow: false, sort: { pending: null, slow: false, last: null } },
  wallet: null,
  chest: null,
  things: [],
  ground: [],
  pickUp: { sent: [], refused: [] },
  thingFrames: {},
  shrines: [],
  shrinePops: 0,
  transitions: 0,
  presenceTransitions: 0,
  pvp: 0,
  worldVersion: null,
  worldSource: null,
  rulesVersion: PAGE_RULES.version,
  objectTypes: null,
  keyboard: "game",
  death: { shown: false, fade: false, cause: null, place: null, ready: false, cues: 0 },
  menuRequests: 0,
  hudRenders: 0,
  name: null,
  players: [],
  playerLooks: () => [],
  creatures: () => [],
  concealment: () => [],
  minimapGround: () => null,
  puddles: () => null,
  moveSpeed: 0,
  minimapOthers: () => ({ players: [], creatures: [], dropped: 0 }),
  worldMap: () => EMPTY_WORLD_MAP_REPORT,
  travel: () => null,
  harvest: () => null,
  belt: () => null,
  concealJudged: 0,
  sightHeading: 0,
  sightDimmed: 0,
  sightCover: 0,
  sightDense: false,
  sightStrength: 0,
  keptCover: 0,
  roofCover: 0,
  silhouette: false,
  weather: { emitters: 0, budget: 0, dim: 0, wisps: 0, dashes: 0, clouds: 0, strength: 0, wet: 0, fading: false, water: 0 },
  audio: createAudioReport(),
  drawn: { x: 0, y: 0, width: 0, height: 0, painted: false, minCx: 0, minCy: 0, maxCx: 0, maxCy: 0, character: false },
  sharpZoom: createSharpZoomControl(),
  lighting: createLightingControl(),
  nameplates: createNameplateReport(),
  healthBars: createHealthBarReport(),
  creaturePlates: createCreaturePlateReport(),
  groundLabels: createGroundLabelReport(),
  damageNumbers: createDamageNumberReport(),
  projectiles: createProjectileReport(),
  bubbles: createBubbleReport(),
  emoji: createEmojiReport(),
  vfx: createVfxReport(),
  prompt: createPromptReport(),
};
